/**
 * Banner prompt to enable push notifications. Shown when push is supported
 * and permission is not yet granted, or when a previous subscription attempt failed.
 * Persists user choice (granted/denied/dismissed) so we don't re-ask every load.
 * When subscription fails we keep showing until they succeed or explicitly dismiss.
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Bell } from 'lucide-react';
import {
  isPushSupported,
  getPermission,
  getStoredPushChoice,
  getPushSubscriptionFailed,
  enablePushNotifications,
  enableTenantPushNotifications,
} from '../../services/pushNotificationService';
import { useToast } from '../ui/Toaster';

const Banner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  background: ${(p) => p.theme.primary || 'hsl(262 83% 58%)'};
  color: white;
  font-size: 14px;
  position: sticky;
  top: 0;
  z-index: 999;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Text = styled.span`
  flex: 1;
`;

const Button = styled.button`
  flex-shrink: 0;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 6px;
  color: white;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

interface NotificationPermissionPromptProps {
  /** When true, prompt is shown when permission is not granted (we keep asking until they enable or deny). When false, never show. */
  showWhenDefault?: boolean;
  /** When set, use tenant push flow (subscribe to tenant backend). Otherwise landlord flow. */
  tenantId?: string;
}

export function NotificationPermissionPrompt({
  showWhenDefault = true,
  tenantId,
}: NotificationPermissionPromptProps) {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRetry, setIsRetry] = useState(false);
  const { success: toastSuccess, error: toastError } = useToast();

  const updateVisibility = React.useCallback(() => {
    if (!showWhenDefault || !isPushSupported()) {
      setVisible(false);
      return;
    }
    const permission = getPermission();
    const stored = getStoredPushChoice();
    const subscriptionFailed = getPushSubscriptionFailed();

    // User explicitly denied in this browser — never show again
    if (stored === 'denied') {
      setVisible(false);
      return;
    }
    // Already granted and subscribed — no banner
    if (permission === 'granted' && !subscriptionFailed) {
      setVisible(false);
      return;
    }
    // Keep asking: show whenever permission is not granted (including after dismiss)
    if (permission !== 'granted' || subscriptionFailed) {
      setIsRetry(!!subscriptionFailed);
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [showWhenDefault]);

  useEffect(() => {
    updateVisibility();
    const onSubscriptionFailed = () => updateVisibility();
    window.addEventListener('tenantplus_push_subscription_failed', onSubscriptionFailed);
    return () => window.removeEventListener('tenantplus_push_subscription_failed', onSubscriptionFailed);
  }, [updateVisibility]);

  const handleEnable = async () => {
    setLoading(true);
    try {
      const ok = tenantId
        ? await enableTenantPushNotifications(tenantId)
        : await enablePushNotifications();
      if (ok) {
        setVisible(false);
        toastSuccess('Notifications enabled', {
          description: tenantId
            ? "You'll receive rent reminders and lease updates."
            : "You'll receive payment and trial reminders.",
        });
      } else {
        const perm = getPermission();
        if (perm === 'denied') {
          setVisible(false);
          toastError('Notifications blocked', {
            description: 'You can enable them later in your browser settings.',
          });
        } else {
          toastError('Could not enable notifications', {
            description: 'Push service error. You can try again later.',
          });
        }
      }
    } catch (e) {
      toastError('Something went wrong', {
        description: (e as Error).message,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <Banner>
      <Bell size={18} style={{ flexShrink: 0 }} />
      <Text>
        {isRetry
          ? tenantId
            ? "Push notifications couldn't be enabled. Try again to get rent reminders and lease updates."
            : "Push notifications couldn't be enabled. Try again to get payment and trial reminders."
          : tenantId
            ? 'Get rent reminders and lease updates — enable notifications.'
            : 'Get payment reminders and trial updates — enable notifications.'}
      </Text>
      <Button type="button" onClick={handleEnable} disabled={loading}>
        {loading ? 'Enabling…' : isRetry ? 'Try again' : 'Enable'}
      </Button>
    </Banner>
  );
}

export default NotificationPermissionPrompt;
